import React, { useState } from "react";
import { Check } from "lucide-react";
import { PrimaryButton } from "./PrimaryButton";
import { CodeInput } from "./CodeInput";

interface CodeStepProps {
  maskedEmail: string;
  isSubmitting: boolean;
  verified: boolean;
  locked: boolean;
  shake: boolean;
  resendIn: number;
  stepAttrs: Record<string, string | undefined>;
  onComplete: (code: string) => void;
  onShakeEnd: () => void;
  onResend: () => void;
  onBack: () => void;
}

const formatCountdown = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const CodeStep: React.FC<CodeStepProps> = ({
  maskedEmail,
  isSubmitting,
  verified,
  locked,
  shake,
  resendIn,
  stepAttrs,
  onComplete,
  onShakeEnd,
  onResend,
  onBack,
}) => {
  const [code, setCode] = useState("");

  return (
    <div
      data-testid="code-step"
      className="login-step space-y-4"
      {...stepAttrs}
    >
      <div className="mb-2">
        <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
          Check your email
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          We sent a 6-digit code to {maskedEmail}.
        </p>
      </div>

      <CodeInput
        onChange={setCode}
        onComplete={onComplete}
        shake={shake}
        onShakeEnd={onShakeEnd}
        disabled={isSubmitting || verified || locked}
      />

      <PrimaryButton
        data-testid="verify-code-btn"
        data-done={verified}
        type="button"
        className="verify-btn relative"
        disabled={code.length < 6 || shake || locked}
        loading={isSubmitting}
        onClick={() => onComplete(code)}
      >
        <span className="verify-label">Verify</span>
        <Check className="verify-tick w-5 h-5 absolute" aria-hidden="true" />
      </PrimaryButton>

      <PrimaryButton
        data-testid="resend-code-btn"
        type="button"
        variant="secondary"
        disabled={resendIn > 0 || locked}
        onClick={onResend}
      >
        {resendIn > 0 ? (
          <span className="tabular-nums">
            Resend code in {formatCountdown(resendIn)}
          </span>
        ) : (
          "Resend code"
        )}
      </PrimaryButton>

      <div className="text-center space-y-1 text-sm text-slate-500 dark:text-slate-400">
        <button
          type="button"
          data-testid="code-back-btn"
          onClick={onBack}
          className="text-indigo-600 dark:text-indigo-400"
        >
          {locked ? "Log in again" : "Use a different email"}
        </button>
        <p>
          Can't open your email?{" "}
          <a
            href="mailto:support@smartstore.com"
            className="text-indigo-600 dark:text-indigo-400"
          >
            Contact support.
          </a>
        </p>
      </div>
    </div>
  );
};
